import { TicketStatsModel } from '../models/ticket-stats.model';
import { TicketStatsUiModel } from '../ui-models/ticket-stats.ui-model';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const BACKLOG_AGES = ['under 1 day', '1 to 3 days', '3 to 7 days', 'over 7 days'];
export const PRIORITIES = ['P1', 'P2', 'P3', 'P4'];

/** "45 min", "3.5 h", "2.1 days"; "—" when nothing was measured. */
export function formatDuration(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  if (minutes < 48 * 60) return `${(minutes / 60).toFixed(1).replace(/\.0$/, '')} h`;
  return `${(minutes / 1440).toFixed(1).replace(/\.0$/, '')} days`;
}

/** The bucket's axis label, from the Cairo date the integrator sends (no time zone maths). */
export function bucketLabel(bucket: string, period: TicketStatsModel['period']): string {
  const [y, m, d] = bucket.split('-').map(Number);
  const month = MONTHS[m - 1] ?? '';
  if (period === 'month') return `${month} ${String(y).slice(2)}`;
  return `${d} ${month}`;
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

class TicketStatsMapper {
  public static mapToUI(model: TicketStatsModel): TicketStatsUiModel {
    const s = model.series ?? [];
    const windowLabel = `last ${model.buckets} ${model.period}s`;
    const backlog = BACKLOG_AGES.map((age) => {
      const byPriority: Record<string, number> = {};
      PRIORITIES.forEach((p) => {
        byPriority[p] = sum(
          model.backlog.filter((r) => r.age === age && r.priority === p).map((r) => r.n)
        );
      });
      return { age, byPriority, total: sum(Object.values(byPriority)) };
    });
    const ch = (k: string) => sum(model.mix.channel.filter((c) => c.key === k).map((c) => c.n));
    return {
      period: model.period,
      windowLabel,
      teams: model.teams,
      team: model.team,
      showsEveryone: model.scope === 'team',
      totals: {
        newTickets: sum(s.map((p) => p.new)),
        newByEmail: sum(s.map((p) => p.new_by_email)),
        handedIn: sum(s.map((p) => p.handed_in)),
        handedOut: sum(s.map((p) => p.handed_out)),
        closed: sum(s.map((p) => p.closed))
      },
      points: s.map((p) => ({
        label: bucketLabel(p.bucket, model.period),
        newTickets: p.new,
        handedIn: p.handed_in,
        closed: p.closed,
        handedOut: p.handed_out
      })),
      firstResponse: formatDuration(model.speed.firstResponseMinutes),
      awaitingResponse: model.speed.awaitingResponse,
      timeToClose: formatDuration(model.speed.closeMinutes),
      openNow: sum(model.backlog.map((r) => r.n)),
      categories: model.mix.category.map((c) => ({
        label: c.code ? `${c.code} · ${c.label.split(' · ').slice(-1)[0]}` : c.label,
        n: c.n
      })),
      segments: model.mix.segment.map((x) => ({ label: x.label, n: x.n })),
      priorities: PRIORITIES.map((key) => ({
        key,
        n: sum(model.mix.priority.filter((p) => p.key === key).map((p) => p.n))
      })),
      byEmail: ch('email'),
      byAdmin: ch('admin'),
      emergencies: sum(model.mix.emergency.filter((e) => e.key === 'yes').map((e) => e.n)),
      satisfaction:
        model.satisfaction?.average !== null && model.satisfaction?.average !== undefined
          ? `${model.satisfaction.average} / 5`
          : '—',
      ratings: model.satisfaction?.count ?? 0,
      backlog,
      people: model.people ?? []
    };
  }
}

export default TicketStatsMapper;
